// The one client module for the room protocol (docs/protocol.md; TS-4, P-1..P-18, EM-1..EM-4, UI-C12).
// It owns the socket: join with the device identity, heartbeat, dead-connection detection, resume with
// last_seq after any drop, seq-gap detection, and the mapping from error codes and close codes to states.
// The room code travels only inside join/resume, never in the URL.
import type { Clock, TimerId } from "../clock";
import { HEARTBEAT_MISSED, HEARTBEAT_MS, RECONNECT_BACKOFF_MS } from "../constants";
import type { IdentityStore } from "./identity";
import type {
  ClientMessage,
  ComparePhraseData,
  EarlierPhrase,
  Knock,
  Listing,
  ListenMode,
  LiveKitGrant,
  MicSettingsData,
  RoomEvent,
  RoomStateData,
  Seat,
  SeatKind,
  ServerMessage,
  TranslationData,
  VoiceTrackClientReason,
  VoiceTrackData,
} from "./types";

export interface SocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: Event) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  onclose: ((ev: CloseEvent) => void) | null;
  onerror: ((ev: Event) => void) | null;
}
export type SocketFactory = (url: string) => SocketLike;

const OPEN = 1;

/** Why a room connection ended for good; each has its own visible state (critique decision 8). */
export type Failure =
  | "room_not_found"
  | "room_closed"
  | "room_full"
  | "rate_limited"
  | "replaced"
  | "server_error"
  /** MAJLIS-80 (ruling e): a host took this seat out. Terminal: never reconnected, no "Try again". */
  | "removed"
  /** MAJLIS-37 (P-26): a host denied this knock (4407). Terminal: no "Try again" - this identity may not knock again. */
  | "denied"
  /** MAJLIS-37 (P-26): nobody answered the knock in time (4411). "Try again" is a fresh knock. */
  | "knock_timeout";

export type ConnStatus = "idle" | "connecting" | "live" | "reconnecting" | "left" | "failed";

export type ClientEvent =
  | { kind: "status"; status: ConnStatus; failure?: Failure }
  /** host (MAJLIS-63): joined/resumed said this seat is the room's host (absent: false). roomCode (MAJLIS-80 ruling
   * (b)): the code the server gave a host (null for anyone else, or while the server does not know it). */
  /** roomId (MAJLIS-126, P-36): joined.room_id - what Your rooms keeps; absent on a resume (the phone has it). */
  | { kind: "seat"; seat: Seat; host: boolean; listenMode: ListenMode; reply: "joined" | "resumed"; roomCode: string | null; roomId?: string | null }
  | { kind: "room_state"; data: RoomStateData }
  /** MAJLIS-9: the media grant that came with joined (a new seat), resumed (a fresh grant, same seat) or the direct
   * reply to our media_grant request (a fresh grant, same seat; null: media is off on this server). */
  /** ref: on a media_grant reply, the ref the server echoed (null: none), to match it to our outstanding request. */
  | { kind: "media"; reply: "joined" | "resumed" | "media_grant"; grant: LiveKitGrant | null; ref?: string | null }
  /** MAJLIS-9: the server refused a media_grant request for now (error media_rate_limited; the socket stays open);
   * ref: the request's ref as the server echoed it (null: none). */
  | { kind: "media_rate_limited"; ref: string | null }
  /** UI-C20 (D-24, D-27): the direct engine_status push: the room's writing paused ("down") or running again ("up"). */
  | { kind: "engine"; state: "up" | "down" }
  /** MAJLIS-37 (P-26): this join is held at a locked room's door (hostPresent: a host is connected), or was let in
   * (`admitted`: joined and room_state follow). */
  | { kind: "knock"; state: "waiting"; hostPresent: boolean }
  | { kind: "knock"; state: "admitted" }
  /** MAJLIS-37 (P-25): to a host - the knocks waiting now, oldest first. */
  | { kind: "knocks"; knocks: Knock[] }
  /** P-10 (MAJLIS-49): a translation for this seat's reading language. */
  | { kind: "translation"; data: TranslationData }
  /** MAJLIS-113 (P-34): this seat is in the isolator's compare mode (since: room time), and one comparison phrase. */
  | { kind: "compare_mode"; since: number }
  | { kind: "compare_phrase"; data: ComparePhraseData }
  /** P-39 (MAJLIS-140): a clip on this passive seat's voice track is playing, has ended, or failed. */
  | { kind: "voice_track"; data: VoiceTrackData }
  /** P-17b (MAJLIS-44): the server refused this seat's set_speak_lang (the socket stays open; nothing changed). */
  | { kind: "speak_lang_refused"; reason: "rate_limited" | "unknown_lang" | null; ref: string | null }
  /** P-19 (MAJLIS-89): the phrases a JOIN was handed (joined.earlier), emitted right after that join's room_state -
   * so the room's own seats take their colours first - and before any room event. Never for a resume. */
  | { kind: "earlier"; phrases: EarlierPhrase[] }
  | { kind: "event"; event: RoomEvent };

export interface JoinParams {
  /** The room code; null for a join by the host link alone (MAJLIS-80), until a host's joined brings the code. */
  room: string | null;
  /** MAJLIS-80 (D-15, EM-18): the host link's secret, sent only inside join (never a URL, never logged). Dropped once
   * the server has recorded the claim: every later join or resume of this identity is host without it. */
  hostSecret?: string;
  /** MAJLIS-126 (P-36): the listing handle of the room chosen in Listed rooms (with `room` for a keyed one). */
  listing?: string;
  /** MAJLIS-126 (P-36, ruling 9): the room id of a Your rooms row - the join names the room by it, never by a code. */
  rejoin?: string;
  name: string;
  speak_lang: string;
  read_lang: string;
  seat_kind: SeatKind;
}

/** P-12 error codes to states. bad_token and identity_mismatch are recovered, not shown. */
export const ERROR_STATES: Readonly<Record<string, Failure>> = {
  room_not_found: "room_not_found",
  room_closed: "room_closed",
  // MAJLIS-80 (G10, ruling e): the host removed this seat - its own terminal state, never reconnected.
  removed: "removed",
  room_full: "room_full",
  rate_limited: "rate_limited",
  replay_unavailable: "server_error",
  internal: "server_error",
  bad_message: "server_error",
  not_joined: "server_error",
  already_joined: "server_error",
};

/** Close codes without a preceding error message (docs/protocol.md "WebSocket close codes"). */
const CLOSE_STATES: Readonly<Record<number, Failure>> = {
  4403: "room_full",
  4404: "room_not_found",
  4406: "removed", // MAJLIS-80: removed by the host (ruling e)
  4407: "denied", // MAJLIS-37: a host denied this knock
  4411: "knock_timeout", // MAJLIS-37: nobody answered this knock in time
  4409: "replaced",
  4410: "room_closed",
  4429: "rate_limited",
  1011: "server_error",
};

/** MAJLIS-64 (D-43(1)): an absent or null listen_mode (an older server) is the server's default, `translated`. */
export function listenModeOf(v: ListenMode | null | undefined): ListenMode {
  return v === "original" ? "original" : "translated";
}

/** Our own close code when we drop a socket to resume (a seq gap or a silent server). */
export const CLIENT_RESUME_CLOSE = 4000;

export function wsUrl(loc: { protocol: string; host: string }): string {
  return `${loc.protocol === "http:" ? "ws" : "wss"}://${loc.host}/ws`;
}

/** MAJLIS-116 (T-141, the `?micdiag=1` panel): told of the room socket's own WebSocket close (never LiveKit's) - its
 * close code and whether it was clean - and nothing else. Given no observer (the default, and always without the
 * flag), nothing extra is read off the CloseEvent. */
export interface SocketObserver {
  close(code: number, clean: boolean): void;
}

export interface RoomClientOptions {
  url: string;
  socket: SocketFactory;
  clock: Clock;
  identity: IdentityStore;
  /** Diagnostics: first failures print with their body (Q-1). Never receives secrets. */
  log?: (message: string, body?: unknown) => void;
  /** MAJLIS-116 (T-141): the `?micdiag=1` panel's socket observer. */
  observer?: SocketObserver;
}

export class RoomClient {
  status: ConnStatus = "idle";
  failure: Failure | undefined;
  /** The highest room seq applied; null until the first room_state gives the baseline. */
  lastSeq: number | null = null;
  seat: Seat | null = null;

  private ws: SocketLike | null = null;
  private params: JoinParams | null = null;
  private token: string | null = null;
  private held: RoomEvent[] = [];
  /** P-19 (MAJLIS-89): joined.earlier, held until that join's room_state has been emitted. */
  private earlier: EarlierPhrase[] | null = null;
  private attempt = 0;
  /** Counter for media_grant refs ("mg-<n>"): never derived from a token or the room code. */
  private mediaRefs = 0;
  /** MAJLIS-80: refs of this seat's remove_seat requests ("rs-<n>"), so their bad_message is recognised and kept quiet. */
  private removeRefs = 0;
  private pendingRemovals = new Set<string>();
  /** MAJLIS-65: refs of this seat's name_voice requests ("nv-<n>"), so their bad_message is recognised and kept quiet. */
  private nameRefs = 0;
  private pendingNames = new Set<string>();
  /** MAJLIS-44: counter for set_speak_lang refs ("sl-<n>"). */
  private speakRefs = 0;
  private everLive = false;
  private leaving = false;
  private renewedIdentity = false;
  private heartbeat: TimerId | undefined;
  private deadTimer: TimerId | undefined;
  private retryTimer: TimerId | undefined;
  private listeners = new Set<(e: ClientEvent) => void>();

  constructor(private readonly opts: RoomClientOptions) {}

  subscribe(fn: (e: ClientEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  join(params: JoinParams): void {
    this.dispose();
    this.params = params;
    this.token = null;
    this.lastSeq = null;
    this.held = [];
    this.earlier = null;
    this.seat = null;
    this.attempt = 0;
    this.everLive = false;
    this.leaving = false;
    this.renewedIdentity = false;
    this.failure = undefined;
    this.pendingRemovals.clear();
    this.pendingNames.clear();
    this.connect();
  }

  mute(): void {
    this.send({ v: 1, type: "mute", data: {} });
  }

  unmute(): void {
    this.send({ v: 1, type: "unmute", data: {} });
  }

  /** P-17 (MAJLIS-62): this seat's Listen. The server records it and enforces it; seat_updated brings it back. */
  setListen(on: boolean): void {
    this.send({ v: 1, type: "set_listen", data: { listen: on ? "on" : "off" } });
  }

  /** P-17 (MAJLIS-63, UI-C17): the host's room mode. The server records it, enforces it and tells every seat
   * (audio_mode_changed is the ONLY thing that changes what the room shows); a change inside the room's interval is
   * refused quietly, anyone but the host is refused not_host. */
  setAudioMode(mode: "live" | "screen_only"): void {
    this.send({ v: 1, type: "set_audio_mode", data: { mode } });
  }

  /** P-24 set_lock (MAJLIS-37, UI-C14): the host locks or unlocks the door. room_updated is what moves the switch. */
  setLock(locked: boolean): void {
    this.send({ v: 1, type: "set_lock", data: { locked } });
  }

  /** P-36 set_listing (MAJLIS-126, UI-C36): the host lists the room or unlists it. room_updated is what moves the radios
   * (never optimistic); a change inside the room's interval is refused quietly, 'open' while locked is listing_locked. */
  setListing(listing: Listing, name?: string): void {
    this.send({ v: 1, type: "set_listing", data: name === undefined ? { listing } : { listing, name } });
  }

  /** P-25 knock_answer (MAJLIS-37): the host admits or denies one waiting knock. The next `knocks` list removes the
   * card on every host device; an answer to a knock no longer waiting is a quiet no-op on the server. */
  answerKnock(identityId: string, admit: boolean): void {
    this.send({ v: 1, type: "knock_answer", data: { identity_id: identityId, admit } });
  }

  /** P-14 close_room (MAJLIS-80, UI-C14): the host ends the majlis for everyone. The server answers every connection,
   * this one included, with room_closed and 4410 - the ended state. Anyone else is refused not_host (recovered). */
  closeRoom(): void {
    this.send({ v: 1, type: "close_room", data: {} });
  }

  /** P-14 remove_seat (MAJLIS-80, UI-C14): the host takes one seat out. seat_left {removed} is what changes the room.
   * The wire does not say which seats are hosts, so a refusal for another host's seat (bad_message) is expected and
   * kept quiet - recognised by this request's own ref, so no other bad_message is ever hidden. */
  removeSeat(seatId: string): void {
    if (!this.seat || !this.ws || this.ws.readyState !== OPEN) return;
    this.removeRefs += 1;
    const ref = `rs-${this.removeRefs}`;
    this.pendingRemovals.add(ref);
    this.send({ v: 1, type: "remove_seat", ref, data: { seat_id: seatId } });
  }

  /** P-16 name_voice (MAJLIS-65, UI-C16): name one voice of a shared seat. voice_named is what changes the room (never
   * optimistic). The name is trimmed here as the server trims it, and an empty one is never sent. A refusal is quiet:
   * not_host (a stale view), or bad_message for THIS request (the voice is gone, or the name was refused) - recognised
   * by its own ref, so no other bad_message is ever hidden. */
  nameVoice(voiceId: string, displayName: string): void {
    const name = displayName.trim();
    if (!name || !this.seat || !this.ws || this.ws.readyState !== OPEN) return;
    this.nameRefs += 1;
    const ref = `nv-${this.nameRefs}`;
    this.pendingNames.add(ref);
    this.send({ v: 1, type: "name_voice", ref, data: { voice_id: voiceId, display_name: name } });
  }

  /** MAJLIS-64 (D-43): this seat's own listen mode. Recorded by the server with no reply and no broadcast; it comes
   * back on the next joined/resumed. No control sends it yet (the UI is built after the owner's review of UI-C21). */
  setListenMode(mode: ListenMode): void {
    this.send({ v: 1, type: "set_listen_mode", data: { mode } });
  }

  /** P-17b (MAJLIS-44, D-34): this seat speaks another language from its next phrase. Sent only on a live, joined
   * seat; seat_updated is the ONLY thing that says it happened (never optimistic) and speak_lang_refused that it did
   * not. Returns whether it was sent. The join parameters follow the SERVER's record (see onRoomEvent), so a rejoin
   * uses whatever language the seat last confirmed. */
  setSpeakLang(tag: string): boolean {
    if (this.status !== "live" || !this.seat || this.leaving || !this.ws || this.ws.readyState !== OPEN) return false;
    this.speakRefs += 1;
    this.send({ v: 1, type: "set_speak_lang", ref: `sl-${this.speakRefs}`, data: { speak_lang: tag } });
    return true;
  }

  /** UI-C9 (MAJLIS-49): read in another language from now on. Remembered for the rest of this seat's life, so a
   * seat that was offline when it was chosen tells the server as soon as it is back (see `syncReadLang`). */
  /** P-39 (MAJLIS-140, ruling d): a PASSIVE seat's microphone settings, once per seat (the app decides once). */
  micSettings(data: MicSettingsData): void {
    this.send({ v: 1, type: "mic_settings", data });
  }

  /** P-39 (MAJLIS-140, condition 5): this passive seat's phone could not hear its voice track. */
  voiceTrackFailed(reason: VoiceTrackClientReason, segmentId?: string): void {
    this.send({
      v: 1,
      type: "voice_track_failed",
      data: segmentId === undefined ? { reason } : { reason, segment_id: segmentId },
    });
  }

  /** P-39 (MAJLIS-144, T-166): this passive seat's track clip started playing, `ms` after its request. */
  voicePlayed(segmentId: string, ms: number): void {
    this.send({ v: 1, type: "voice_played", data: { segment_id: segmentId, request_to_playing_ms: ms } });
  }

  setReadLang(tag: string): void {
    if (!this.params) return;
    this.params = { ...this.params, read_lang: tag };
    // `this.seat` is the seat as joined, not as updated since: every choice is sent, the server's reply is cheap
    if (this.seat) this.send({ v: 1, type: "set_read_lang", data: { read_lang: tag } });
  }

  /** The server's seat reads what this device chose: after a join (an existing seat keeps its old language) or a
   * resume (the choice may have been made while the socket was down), say so once. */
  private syncReadLang(seat: Seat): void {
    const want = this.params?.read_lang;
    if (want && seat.read_lang !== want) this.send({ v: 1, type: "set_read_lang", data: { read_lang: want } });
  }

  /** MAJLIS-9 (consultant 2026-09-19): ask for a fresh LiveKit grant for this seat (LiveKit gave the connection up
   * while this socket stayed live). Sent only on a live, joined seat, with a fresh ref (MAJLIS-22) that is returned so
   * the reply can be matched to it; null when it was not sent (the resume path then brings a fresh grant). */
  requestMediaGrant(): string | null {
    if (this.status !== "live" || !this.seat || this.leaving || !this.ws || this.ws.readyState !== OPEN) return null;
    this.mediaRefs += 1;
    const ref = `mg-${this.mediaRefs}`;
    this.send({ v: 1, type: "media_grant", ref, data: {} });
    return ref;
  }

  /** MAJLIS-12, EM-23: the seat token, for the Authorization header of GET /api/audio/{segment_id} only (never a URL,
   * never a log). null before joined, and after a bad_token until the next join. */
  seatToken(): string | null {
    return this.token;
  }

  leave(): void {
    this.leaving = true;
    if (this.ws && this.ws.readyState === OPEN) {
      this.send({ v: 1, type: "leave", data: {} });
      return; // the server answers with seat_left and closes 1000
    }
    this.stopTimers();
    this.ws = null;
    this.setStatus("left");
  }

  /** Close without reconnecting (the page is going away or a new join starts). */
  dispose(): void {
    this.stopTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) ws.close(1000);
    this.status = "idle";
  }

  // ---------------------------------------------------------------- socket lifecycle

  private connect(): void {
    this.retryTimer = undefined;
    this.setStatus(this.everLive ? "reconnecting" : "connecting");
    const ws = this.opts.socket(this.opts.url);
    this.ws = ws;
    ws.onopen = () => {
      if (ws !== this.ws) return;
      this.armDeadTimer();
      this.heartbeat = this.opts.clock.setInterval(
        () => this.send({ v: 1, type: "ping", data: {} }),
        HEARTBEAT_MS,
      );
      this.sendHello();
    };
    ws.onmessage = (ev) => {
      if (ws !== this.ws) return;
      this.armDeadTimer();
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        this.log("unparseable server frame", ev.data);
        return;
      }
      this.handle(msg);
    };
    ws.onclose = (ev) => {
      if (ws !== this.ws) return;
      this.opts.observer?.close(ev.code, ev.wasClean); // MAJLIS-116 (T-141): observation only, before onClosed below
      this.ws = null;
      this.onClosed(ev.code);
    };
    ws.onerror = () => {
      // a close event always follows
    };
  }

  private sendHello(): void {
    const p = this.params!;
    // MAJLIS-80: a seat that joined by the host link alone and has not been given the code resumes by its token, and
    // names no room; the host secret goes only in a join, and only until the server has recorded the claim.
    const room = p.room === null ? {} : { room: p.room };
    if (this.token) {
      this.send({ v: 1, type: "resume", data: { ...room, seat_token: this.token, last_seq: this.lastSeq ?? 0 } });
    } else {
      this.send({
        v: 1,
        type: "join",
        data: {
          ...room,
          ...(p.hostSecret ? { host_secret: p.hostSecret } : {}),
          ...(p.listing ? { listing: p.listing } : {}), // MAJLIS-126 (P-36)
          ...(p.rejoin ? { rejoin: p.rejoin } : {}),
          name: p.name,
          speak_lang: p.speak_lang,
          read_lang: p.read_lang,
          seat_kind: p.seat_kind,
          identity: this.opts.identity.get(),
        },
      });
    }
  }

  private onClosed(code: number): void {
    this.stopTimers();
    if (this.leaving) return this.setStatus("left");
    if (this.failure) return this.setStatus("failed", this.failure);
    if (code === 1000) return this.setStatus("left");
    const failure = CLOSE_STATES[code];
    if (failure) {
      this.failure = failure;
      return this.setStatus("failed", failure);
    }
    if (code === 4401) this.token = null; // the seat token is gone: join again with the identity
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    const delay = RECONNECT_BACKOFF_MS[Math.min(this.attempt, RECONNECT_BACKOFF_MS.length - 1)];
    this.attempt += 1;
    this.setStatus(this.everLive ? "reconnecting" : "connecting");
    this.retryTimer = this.opts.clock.setTimeout(() => this.connect(), delay);
  }

  /** Drop this socket on purpose and resume on a fresh one (seq gap, silent server). */
  private resumeNow(reason: string): void {
    this.log(`resuming: ${reason}`, { lastSeq: this.lastSeq });
    this.stopTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) ws.close(CLIENT_RESUME_CLOSE, reason);
    this.scheduleReconnect();
  }

  private armDeadTimer(): void {
    if (this.deadTimer !== undefined) this.opts.clock.clearTimeout(this.deadTimer);
    this.deadTimer = this.opts.clock.setTimeout(
      () => this.resumeNow("no message from the server for 2 heartbeat intervals"),
      HEARTBEAT_MS * HEARTBEAT_MISSED,
    );
  }

  private stopTimers(): void {
    const c = this.opts.clock;
    if (this.heartbeat !== undefined) c.clearInterval(this.heartbeat);
    if (this.deadTimer !== undefined) c.clearTimeout(this.deadTimer);
    if (this.retryTimer !== undefined) c.clearTimeout(this.retryTimer);
    this.heartbeat = this.deadTimer = this.retryTimer = undefined;
  }

  // ---------------------------------------------------------------- messages

  private handle(msg: ServerMessage): void {
    if (typeof msg.seq === "number") return this.onRoomEvent(msg as RoomEvent);
    switch (msg.type) {
      case "joined": {
        this.token = msg.data.seat_token;
        this.seat = msg.data.seat;
        // P-19: an older server sends no list (D-46), and an empty one draws nothing
        this.earlier = Array.isArray(msg.data.earlier) ? msg.data.earlier : null;
        this.attempt = 0;
        const roomCode = this.learnCode(msg.data.host === true, msg.data.room_code);
        this.emit({ kind: "seat", seat: msg.data.seat, host: msg.data.host === true, listenMode: listenModeOf(msg.data.listen_mode), reply: "joined", roomCode, roomId: msg.data.room_id ?? null });
        this.emit({ kind: "media", reply: "joined", grant: msg.data.livekit ?? null });
        this.syncReadLang(msg.data.seat);
        return;
      }
      case "resumed": {
        this.seat = msg.data.seat;
        this.earlier = null; // P-19: a resume carries no list, and never re-sends one
        this.attempt = 0;
        const roomCode = this.learnCode(msg.data.host === true, msg.data.room_code);
        this.emit({ kind: "seat", seat: msg.data.seat, host: msg.data.host === true, listenMode: listenModeOf(msg.data.listen_mode), reply: "resumed", roomCode });
        this.emit({ kind: "media", reply: "resumed", grant: msg.data.livekit ?? null });
        this.syncReadLang(msg.data.seat);
        return;
      }
      case "media_grant":
        this.emit({ kind: "media", reply: "media_grant", grant: msg.data.livekit ?? null, ref: msg.ref ?? null });
        return;
      case "room_state":
        return this.onRoomState(msg.data);
      case "engine_status":
        this.emit({ kind: "engine", state: msg.data.state });
        return;
      case "translation":
        this.emit({ kind: "translation", data: msg.data });
        return;
      case "compare_mode": // MAJLIS-113 (P-34): to this seat only, and only when it is compared
        this.emit({ kind: "compare_mode", since: msg.data.since });
        return;
      case "compare_phrase":
        this.emit({ kind: "compare_phrase", data: msg.data });
        return;
      case "voice_track": // MAJLIS-140 (P-39): to a passive seat only
        this.emit({ kind: "voice_track", data: msg.data });
        return;
      case "waiting": // MAJLIS-37 (P-26): held at the door; the socket stays open, heartbeats keep it alive
        this.attempt = 0;
        this.emit({ kind: "knock", state: "waiting", hostPresent: msg.data.host_present === true });
        return;
      case "admitted": // MAJLIS-37: let in - `joined` and `room_state` follow exactly as for any join
        this.emit({ kind: "knock", state: "admitted" });
        return;
      case "denied": // MAJLIS-37: the server closes 4407 next; this identity may not knock here again
        this.failure = "denied";
        return;
      case "knock_timeout": // MAJLIS-37: the server closes 4411 next; Try again is a fresh knock
        this.failure = "knock_timeout";
        return;
      case "knocks": // MAJLIS-37 (P-25): to a host only
        this.emit({ kind: "knocks", knocks: msg.data.knocks });
        return;
      case "error":
        return this.onError(msg.data.code, msg.data, msg.ref ?? null);
      case "pong":
        return;
      default:
        this.log("unknown server message", msg);
    }
  }

  /** MAJLIS-80 ruling (b): a host's joined/resumed may carry the room code. From then on this seat's join and resume
   * name the room by it (so a server that restarted learns it back), and a proven host link is not sent again - the
   * server holds the claim by identity. Returns the code to show, or null (a guest, or a code not yet known). */
  private learnCode(host: boolean, code: string | null | undefined): string | null {
    if (!host || !this.params) return null;
    if (this.params.hostSecret) this.params = { ...this.params, hostSecret: undefined };
    // MAJLIS-126: a host that came in by a handle or by its room id names the room by its code from now on (a rejoin
    // never goes with a code, P-36)
    if (typeof code === "string" && code) this.params = { ...this.params, room: code, listing: undefined, rejoin: undefined };
    return this.params.room;
  }

  private onRoomState(data: RoomStateData): void {
    if (this.lastSeq === null) {
      this.lastSeq = data.seq;
      const held = this.held;
      this.held = [];
      this.emit({ kind: "room_state", data });
      this.flushEarlier();
      this.markLive();
      for (const ev of held) this.onRoomEvent(ev);
      return;
    }
    if (data.seq > this.lastSeq) {
      // the room is ahead of what we applied: fetch the missing events instead of skipping them
      this.emit({ kind: "room_state", data });
      this.flushEarlier();
      return this.resumeNow(`room at seq ${data.seq}, applied ${this.lastSeq}`);
    }
    this.emit({ kind: "room_state", data });
    this.flushEarlier();
    this.markLive();
  }

  /** P-19 (MAJLIS-89): hand the join's earlier phrases on once, after its room_state. The model drops any it already
   * holds (a join again after a lost token), so nothing is ever drawn twice. */
  private flushEarlier(): void {
    const phrases = this.earlier;
    this.earlier = null;
    if (phrases && phrases.length > 0) this.emit({ kind: "earlier", phrases });
  }

  private onRoomEvent(ev: RoomEvent): void {
    if (this.lastSeq === null) {
      this.held.push(ev);
      return;
    }
    if (ev.seq <= this.lastSeq) return; // already applied (a replay overlapping the live stream)
    if (ev.seq !== this.lastSeq + 1) return this.resumeNow(`seq gap: got ${ev.seq}, expected ${this.lastSeq + 1}`);
    this.lastSeq = ev.seq;
    // MAJLIS-44: this seat's speaking language as the server recorded it is what a later join (Rejoin, a lost token)
    // must carry - never the entry screen's older choice.
    if (ev.type === "seat_updated" && this.params && ev.data.seat.id === this.seat?.id)
      this.params = { ...this.params, speak_lang: ev.data.seat.speak_lang };
    this.emit({ kind: "event", event: ev });
  }

  private onError(code: string, body: unknown, ref: string | null): void {
    if (code === "identity_mismatch" && !this.renewedIdentity) {
      // DM-0: mint a new identity and join again on the same connection (it stays open)
      this.renewedIdentity = true;
      this.opts.identity.renew();
      this.sendHello();
      return;
    }
    if (code === "bad_token") {
      this.token = null; // the server closes 4401; the reconnect joins with the identity
      return;
    }
    if (code === "media_rate_limited") {
      // MAJLIS-9: a media_grant request was refused for now; the room itself is fine and the socket stays open
      this.log(`server error ${code}`, body);
      this.emit({ kind: "media_rate_limited", ref });
      return;
    }
    if (code === "bad_message" && ref !== null && this.pendingRemovals.delete(ref)) {
      // MAJLIS-80: our remove_seat was refused - the seat is the host's own or another host's (the wire does not say
      // which seats are hosts), or it has already gone. The room and the socket are fine.
      this.log(`server error ${code}`, body);
      return;
    }
    if (code === "bad_message" && ref !== null && this.pendingNames.delete(ref)) {
      // MAJLIS-65: our name_voice was refused (no such voice any more, or the name was out of bounds). The room and the
      // socket are fine; the label simply keeps its name.
      this.log(`server error ${code}`, body);
      return;
    }
    if (code === "speak_lang_refused") {
      // MAJLIS-44 (P-17b): our set_speak_lang was refused (too soon, or not a speaking language). The seat keeps its
      // language and the socket stays open; the speak picker says so (never a reason to end the room).
      this.log(`server error ${code}`, body);
      const reason = (body as { reason?: unknown } | null)?.reason;
      this.emit({
        kind: "speak_lang_refused",
        reason: reason === "rate_limited" || reason === "unknown_lang" ? reason : null,
        ref,
      });
      return;
    }
    if (code === "listing_locked") {
      // MAJLIS-126 (P-36): 'open' was asked while the room is locked - the control never offers it then, so this is a
      // stale view; the room and the socket are fine, and the radios keep showing the server's record
      this.log(`server error ${code}`, body);
      return;
    }
    if (code === "not_host") {
      // MAJLIS-63: a host-only message was refused; the seat and the socket are fine. Only the host is shown the
      // switch, so this is a stale view (or another device), never a reason to end the room.
      this.log(`server error ${code}`, body);
      return;
    }
    const failure = ERROR_STATES[code] ?? "server_error";
    this.log(`server error ${code}`, body);
    this.failure = failure;
    // errors that keep the socket open (bad_message, not_joined, already_joined) end it from our side
    if (this.ws && !["room_not_found", "room_closed", "room_full", "rate_limited", "removed"].includes(failure)) {
      const ws = this.ws;
      this.ws = null;
      this.stopTimers();
      ws.close(1000);
      this.setStatus("failed", failure);
    }
  }

  private markLive(): void {
    this.everLive = true;
    this.setStatus("live");
  }

  private send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === OPEN) this.ws.send(JSON.stringify(msg));
  }

  private setStatus(status: ConnStatus, failure?: Failure): void {
    if (this.status === status && !failure) return;
    this.status = status;
    this.emit({ kind: "status", status, failure });
  }

  private emit(e: ClientEvent): void {
    for (const fn of this.listeners) fn(e);
  }

  private log(message: string, body?: unknown): void {
    this.opts.log?.(message, body);
  }
}

// ------------------------------------------------------------------ room creation (HTTP)

export interface CreatedRoom {
  code: string;
  join_url: string;
  host_link: string;
}

export type CreateResult = { ok: true; room: CreatedRoom } | { ok: false; failure: Failure };

/** MAJLIS-137 (P-38): a passive room's creation answer - the host link ONLY (no code, no join link). */
export interface CreatedPassiveRoom {
  host_link: string;
}

/** POST /api/rooms (docs/protocol.md "Room creation"). identity_mismatch mints a new identity once. */
export async function createRoom(fetchImpl: typeof fetch, identity: IdentityStore): Promise<CreateResult> {
  return postRoom<CreatedRoom>(fetchImpl, identity, {});
}

/** MAJLIS-137 (P-38, D-57): POST /api/rooms with `passive: true`; the phone then joins by the host link's secret. */
export async function createPassiveRoom(
  fetchImpl: typeof fetch,
  identity: IdentityStore,
): Promise<{ ok: true; room: CreatedPassiveRoom } | { ok: false; failure: Failure }> {
  return postRoom<CreatedPassiveRoom>(fetchImpl, identity, { passive: true });
}

async function postRoom<T>(
  fetchImpl: typeof fetch,
  identity: IdentityStore,
  extra: Record<string, unknown>,
): Promise<{ ok: true; room: T } | { ok: false; failure: Failure }> {
  for (let tries = 0; tries < 2; tries++) {
    let res: Response;
    try {
      res = await fetchImpl("/api/rooms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identity: identity.get(), ...extra }),
      });
    } catch {
      return { ok: false, failure: "server_error" };
    }
    if (res.status === 201) return { ok: true, room: (await res.json()) as T };
    if (res.status === 403 && tries === 0) {
      identity.renew();
      continue;
    }
    return { ok: false, failure: res.status === 429 ? "rate_limited" : "server_error" };
  }
  return { ok: false, failure: "server_error" };
}
